import { Component, OnInit, DoCheck } from '@angular/core';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { InsightsState } from 'src/app/components/insights/redux/insights.state';
import { QaServiceService } from 'src/app/core/services/qa-service.service';
import { Campaign } from 'src/app/models/campaign';
import { Insight } from 'src/app/models/insight';
import { Robot } from 'src/app/models/robot';
import { SpinnerState } from 'src/app/shared/spinner/redux/spinner.state';
import { CampaignState } from '../../../campaigns/redux';
import { QaState } from '../../redux';
import { ChangedSpaceEvent } from 'src/app/models/qa-models/changed-space-event';
import { FavoriteRecommendation } from 'src/app/models/qa-models/favorite-recommendation';
import { UtilityService } from 'src/app/core/services/utility.service';
import { ImageReview } from 'src/app/models/qa-models/image-review';
import { ObjectZoneRecommendation, ObservedZone } from 'src/app/models/qa-models/object-zone-recommendation';
import { NbToastrService } from '@nebular/theme';
import {
  SmartScheduleRecommendation,
  RegionRecommendations,
  RegionSchedule
} from 'src/app/models/qa-models/schedule-recommendation';
import { QAInsightEvent } from 'src/app/models/qa-models';

@Component({
  selector: 'app-common-bots',
  templateUrl: './common-bots.component.html',
  styleUrls: ['./common-bots.component.css']
})
export class CommonBotsComponent implements OnInit, DoCheck {
  @Select(CampaignState.campaign) public data_campaigns$: Observable<any[]>;
  @Select(InsightsState.insightsActive) public data_insights$: Observable<any>;
  @Select(QaState.robots) public data_allRobots$: Observable<any[]>;
  @Select(QaState.ownerRobots) public data_ownerRobots$: Observable<any[]>;
  @Select(SpinnerState) public loading: Observable<boolean>;
  public spin;
  public formRobotData: Robot[];
  public default_timezone = 'America/Chicago';
  public insights: Insight[];

  public selectedRobots: any[] = [];
  public campaigns: Campaign[];

  public ownerRobots = [];
  public selectedCampaigns = {};

  public additionalFormValues;
  public regions = {};

  public submitBtn_enable = false;
  public submitBtn_show = false;

  constructor(
    public service: QaServiceService,
    private toastrService: NbToastrService,
    public utility: UtilityService
  ) {}

  public ngOnInit() {
    this.data_insights$.subscribe((data) => (this.insights = data));
    this.data_allRobots$.subscribe((data) => {
      this.formRobotData = JSON.parse(JSON.stringify(data));
    });
    this.data_campaigns$.subscribe((data) => {
      this.campaigns = data.slice().sort((a, b) => (a.created_dttm > b.created_dttm ? -1 : 1));
    });
    this.data_ownerRobots$.subscribe((data) => {
      this.ownerRobots = JSON.parse(JSON.stringify(data));
    });
  }

  public ngDoCheck(): void {
    //Called every time that the input properties of a component or a directive are checked. Use it to extend change detection by performing a custom check.
    //Add 'implements DoCheck' to the class.

    this.submitBtn_show = this.showSubmitButton();
    this.submitBtn_enable = this.disableSubmitButton();
  }

  /******************************************************
   * Submit button enabling functions for both common
   * and KOZR robots
   *****************************************************/
  public showSubmitButton() {
    let verify = false;

    if (this.selectedRobots.length > 0) {
      verify = this.selectedRobots.every(
        (robot) => this.selectedCampaigns[robot.id] && this.selectedCampaigns[robot.id].length !== 0
      );
    }
    // This resets the "Notification submission successful" modal to null
    // before the next submission takes place
    if (!verify) {
      this.service.submittedNotification = null;
    }

    return verify;
  }

  /******************************************************
   * Serves to check if the submit button should be
   * enabled in the HTML or not
   *****************************************************/
  public disableSubmitButton() {
    return this.selectedRobots.some((robot) => {
      if (!this.selectedCampaigns[robot.id] || this.selectedCampaigns[robot.id].length === 0) {
        return true;
      }

      return false;
    });
  }
  public groupByFn() {
    return (item) => {
      if (this.ownerRobots.find((s) => s.id === item.id)) {
        return 'My Robots';
      }

      return 'Common Robots';
    };
  }

  public onScheduleChanged(robot, regions) {
    this.regions[robot.id] = regions;
  }

  public onRegionChanged(robot, regions) {
    this.regions[robot.id] = regions;
  }

  /******************************************************
   * Submit notification method.
   *
   * This method uses the qa-service to parse the
   * notification, which is then also parsed through
   * to utility.service for a different endpoint.
   *****************************************************/
  public createQANotification() {
    this.service.submittedNotification = null;
    const notifications = [];
    this.selectedRobots.map((robot) => {
      this.selectedCampaigns[robot.id].map((campaign) => {
        try {
          const notification = this.createDefaultQANotification(campaign.id, robot);
          switch (campaign.campaign_type) {
            case 'SpaceChanged':
              const changedSpaceEventObject = this.createChangedSpaceEventObject();
              notification.changed_space_event = changedSpaceEventObject;

              break;
            case 'ImageReview':
              const imageReviewObject = this.createImageReviewObject();
              notification.image_review = imageReviewObject;

              break;
            case 'ObjectZoneRecommendation':
              const objectZoneRecommendationObject = this.createObjectZoneRecommendationObject();
              notification.object_zone_recommendation = objectZoneRecommendationObject;

              break;
            case 'SmartScheduleRecommendation':
              const campaign_version = campaign.name.includes('v1') || campaign.name.includes('V1') ? '1.0' : '2.0';
              const scheduleRecommendation = this.createScheduleRecommendationObject(robot.id);
              // v1 and v2 differentiation done by campaign name
              scheduleRecommendation.schedulingVersion = campaign_version;
              notification.schedule_recommendation = scheduleRecommendation;

              break;
            case 'FavoriteRecommendation':
              const favoriteRecommendation = this.createFavoriteRecommendationObject();
              notification.favorite_recommendation = favoriteRecommendation;

              break;
            default:
              break;
          }
          this.service.submitNotification(notification);

          notifications.push(notification);
        } catch (error) {
          console.error(error);
          this.toastrService.show(status, 'Invalid input, check the input parameters!', { status: 'warning' });
        }
      });
    });

    return notifications;
  }

  public createChangedSpaceEventObject() {
    const changedSpaceEvent = new ChangedSpaceEvent();
    changedSpaceEvent.pmap_id = this.additionalFormValues[0];
    changedSpaceEvent.pmapv_id = this.additionalFormValues[1];
    changedSpaceEvent.change_type = this.additionalFormValues[2];
    changedSpaceEvent.mission_number = this.additionalFormValues[3];
    changedSpaceEvent.mission_timestamp = this.additionalFormValues[4];

    return changedSpaceEvent;
  }

  public createFavoriteRecommendationObject() {
    const favoriteRecommendation = new FavoriteRecommendation();
    favoriteRecommendation.id = this.utility.createRandomMockID();
    favoriteRecommendation.display_order = 0;
    favoriteRecommendation.modification_secs = 0;
    favoriteRecommendation.robot_commanddefs = [];
    favoriteRecommendation.commanddefs = this.additionalFormValues.commanddefs;
    favoriteRecommendation.color = '#FFFFFF';

    return favoriteRecommendation;
  }

  public createImageReviewObject() {
    const imageReview = new ImageReview();
    imageReview.mission_id = this.additionalFormValues[0];

    return imageReview;
  }

  public createObzervedZone() {
    const zone = new ObservedZone();
    zone.zone_id = this.additionalFormValues[2];
    zone.region_id = this.additionalFormValues[3];
    zone.primary_related_object = this.additionalFormValues[4];
    zone.primary_object_type = this.additionalFormValues[5];
    zone.suggestion_id = this.additionalFormValues[6];
    zone.confidence = this.additionalFormValues[7];

    return [zone];
  }

  public createObjectZoneRecommendationObject() {
    const objectZoneRecommendation = new ObjectZoneRecommendation();
    objectZoneRecommendation.pmap_id = this.additionalFormValues[0];
    objectZoneRecommendation.pmapv_id = this.additionalFormValues[1];
    objectZoneRecommendation.last_user_pmapv_id = this.additionalFormValues[1];
    const zones = this.createObzervedZone();
    objectZoneRecommendation.zones = zones;

    return objectZoneRecommendation;
  }

  public createScheduleRecommendationObject(robot_id: string) {
    const scheduleRecommendation = new SmartScheduleRecommendation();
    scheduleRecommendation.id = this.utility.createRandomMockID();
    scheduleRecommendation.robotId = robot_id;
    scheduleRecommendation.timezone = this.default_timezone;
    const recommendedSchedule = this.createRegionRecommendations(robot_id);
    scheduleRecommendation.recommendedSchedule = recommendedSchedule;

    return scheduleRecommendation;
  }

  public createRegionRecommendations(robot_id: string) {
    const recommendedSchedule = new RegionRecommendations();

    const schedules = this.regions[robot_id].regions.map((region) => {
      const regionSchedule = new RegionSchedule();
      regionSchedule.pmap_id = region.pmap_id;
      regionSchedule.pmapv_id = region.pmapv_id;
      regionSchedule.region_id = region.region_id !== '' ? region.region_id : '__all__';
      regionSchedule.schedule = this.createSchedule(region);

      return regionSchedule;
    });

    recommendedSchedule.regions = schedules;

    return recommendedSchedule;
  }

  public createSchedule(region) {
    const schedule = {
      Mon: { start_time: null, score: null },
      Tue: { start_time: null, score: null },
      Wed: { start_time: null, score: null },
      Thur: { start_time: null, score: null },
      Fri: { start_time: null, score: null },
      Sat: { start_time: null, score: null },
      Sun: { start_time: null, score: null }
    };
    schedule[region.schedule.day].start_time = region.schedule.hour * 3600 + region.schedule.minute * 60;
    schedule[region.schedule.day].score = parseInt(region.schedule.score, 10);

    return schedule;
  }

  public createDefaultQANotification(campaign_id: string, robot: Robot) {
    const notification = new QAInsightEvent();
    notification.campaign_id = campaign_id;
    notification.robot_id = robot.id;
    notification.robot_name = robot.name;

    return notification;
  }

  /******************************************************
   * Receive values from an auto-generated form
   *****************************************************/
  public receiveChildParameters(event) {
    this.additionalFormValues = event;
  }
}
